import { useTranslation } from 'react-i18next';
import { graphql } from 'gatsby';

import TwoColumnL30R70 from '@klaviyo-mkt/gatsby-components/utils/two-column-l30-r70';
import GlobalHead from '../../components/head';
import Layout from '../../layout';

import { topArticles as topArticlesList } from './data';

import * as s from './styles';

import type { HeadProps, PageProps } from 'gatsby';
import type { IPageContext } from '../../components/head/types';
import type { IHCLayout } from '../../layout/types';

const getFullArticleCount = (sectionData: Queries.Section[]) => sectionData.reduce((sectionArticleCount: number, currentSection: Queries.Section) => currentSection?.articles?.length ? (sectionArticleCount + currentSection.articles.length) : sectionArticleCount, 0);

export const Category = ({ data: { allSection, category, topArticles }, location, ...pageProps  }: PageProps<Queries.CategoryQuery>) => {
  const { t } = useTranslation();
  
  if (category === null) {
    return null;
  }

  const categoryTopArticles = topArticlesList[category.sourceId as keyof typeof topArticlesList] ?? [];
  const sortedTopArticles = topArticles.nodes
    .slice()
    .filter((topArticle) => topArticle.sourceId !== null)
    .sort((a, b) => categoryTopArticles.indexOf(a.sourceId as string) - categoryTopArticles.indexOf(b.sourceId as string));

  return (
    <Layout
      {...pageProps}
      location={location}
      translations={category?.translations?.filter(Boolean) as any as IHCLayout['translations']}
    >
      <s.CategoryPage>
        <TwoColumnL30R70
          headline={t('helpCenter_templates_home_twoColumnL30R70_headline')}
          childrenLeft={<s.StyledTopicsMenu selectedTopic={category.name || ''} />}
          childrenRight={(
            <s.Content>
              <s.CategoryMeta>
                <s.CategoryName colorVariant="dark" forwardedAs="h1" variant="h5Semibold">{category.name}</s.CategoryName>
                <s.CategoryDescription colorVariant="dark" variant="h6">{category.description}</s.CategoryDescription>
              </s.CategoryMeta>
              <s.Sections>
                {allSection?.nodes?.map(({ articles, name, parentSection, sourceId, locale }) => {
                  if ((articles === null) || (name === null) || (sourceId === null)) return null;
                  const fullArticleCount = getFullArticleCount(allSection?.nodes?.filter((section) => section.parentSection && section.parentSection.sourceId === sourceId && section.locale === category.locale) as Queries.Section[]) + articles.length;
                  if (parentSection !== null || fullArticleCount === 0 || locale !== category.locale) return;
                  return (
                    <s.Section
                      headlineVariant="paragraph1Semibold"
                      articleCount={fullArticleCount}
                      key={name}
                      name={name}
                      link={`/hc/${category?.locale}/sections/${sourceId}`}
                    />
                  );
                })}
              </s.Sections>
              {(categoryTopArticles.length > 0) && (topArticles?.nodes?.length > 0) &&
                <>
                  <s.TopArticleHeadline variant='h6Semibold' forwardedAs='h2'>
                    {t('helpCenter_templates_category_topArticles')}
                  </s.TopArticleHeadline>
                  {sortedTopArticles.map((topArticle) => (
                    <s.TopArticleUl key={topArticle.url}>
                      <s.TopArticleList>
                        <s.TopArticle to={topArticle.url ?? ''}>{topArticle.title}</s.TopArticle>
                      </s.TopArticleList>
                    </s.TopArticleUl>
                  ))}
                </>
              }
            </s.Content>
          )}
        />
      </s.CategoryPage>
    </Layout>
  );
};

export default Category;

export const Head = ({ location, pageContext, data: { category } }: HeadProps<Queries.CategoryQuery, IPageContext>) => (
  <GlobalHead
    langCode={pageContext.locale}
    pathname={location.pathname}
    name={category?.name || ''}
    pageType="category"
    seo={{
      canonical: `https://help.klaviyo.com/hc/${category?.locale}/categories/${category?.sourceId}`,
    }}
    translations={category?.translations?.filter(Boolean) as IHCLayout['translations']}
  />
);

export const query = graphql`
  query Category($compositeId: String, $id: String, $locale: String, $topArticles: [String]) {
    category(id: { eq: $id }) {
      compositeId
      description
      draft
      htmlUrl
      id
      locale
      name
      position
      slug
      sourceId
      sourceLocale
      translations {
        hreflang
        locale
        name
        url
      }
    }
    allSection(
        filter: {
          category: {compositeId: {eq: $compositeId}}
          locale: {eq: $locale}
        },
        sort: {position: ASC}
      ) {
      nodes {
        articles {
          id
          compositeId
          locale
        }
        compositeId
        id
        name
        locale
        parentSection {
          id
          sourceId
          locale
        }
        slug
        sourceId
      }
    }
    topArticles: allArticle(
      filter: {
        locale: {eq: $locale}
        sourceId: {in: $topArticles}
      }
    ) {
      nodes {
        sourceId
        title
        url
      }
    }
  }
`;
